<template>
  <Teleport to="#app">
    <div class="modal-wrap" v-if="isOpen" @click.self="onClickOutside" data-qa="info-modal-overlay-close">
      <div class="modal" :class="modalSize">
        <div class="info-modal">
          <div class="info-modal-head">
            <div class="info-modal-title" data-qa="info-modal-title">
              <vita-icon v-if="modalIcon" :name="modalIcon"/>
              <div>{{title}}</div>
            </div>
            <div class="info-modal-close"
                 :class="{ 'btn-disabled': preventClose }"
                 @click="confirmClose ? (showConfirm = true) : close()"
                 data-qa="info-modal-icon-close"
            >
              <vita-icon name="cross-dark"/>
            </div>
          </div>
          <div class="info-modal-body">
            <slot :showConfirm="showConfirm"></slot>
            <div class="confirm-close" v-if="showConfirm">
              <div>
                <h2>{{ confirmCloseTitle }}</h2>
                <div>{{ confirmClose }}</div>
              </div>
              <div>
                <vita-button class="rounded no-shadow" color="red" width="inline" @click="close"
                  data-qa="info-modal-button-confirm">Да</vita-button>
                <vita-button class="rounded no-shadow" color="blue" width="inline" @click="showConfirm = false"
                  data-qa="info-modal-button-cancel">Нет</vita-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue';
import VitaButton from './VitaButton.vue';

interface Props {
  title: string
  modalIcon: string | boolean
  modalSize?: string
  open: boolean
  preventClose?: boolean
  confirmClose?: string
  confirmCloseTitle?: string
  closeOnClickOutside?: boolean
}

const props = defineProps<Props>()
const isOpen = ref<boolean>(props.open)
const showConfirm = ref(false)
const emit = defineEmits(['toggleInfoModal', 'open', 'close']);

const close = () => {
  showConfirm.value = false
  emit('close')
  emit('toggleInfoModal')
}

const onClickOutside = () => {
  if (!props.closeOnClickOutside || props.preventClose) return
  if (props.confirmClose) {
    showConfirm.value = true
  } else {
    close()
  }
}

watch(props, (val: Props) => {
  isOpen.value = val.open
  if (val.open) {
    emit('open')
  } else {
    showConfirm.value = false
  }
})
</script>

<script lang="ts">
export default {
  name: 'vita-info-modal'
}
</script>

<style scoped lang="scss">
@use '../../static/style/variables.scss' as *;
@use '../../static/style/mixins.scss' as *;

.modal-wrap {
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(2.5px);
  display: flex;
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  z-index: 9999;
}

.modal {
  margin: auto;
  max-height: 100vh;
  min-height: 600px;
  overflow: hidden;

  &::-webkit-scrollbar {
    width: 7px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(71,71,71, .8);
    border-radius: 20px;
  }
}

.info-modal {
  margin: 20px;
  background: #fff;
  max-width: 950px;
  border-radius: 3px;
  box-shadow: -1px 0px 12px rgba(0, 0, 0, 0.15);
  position: relative;

  &-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 24px;
    background: #DDDDDD;
    border-radius: 3px 3px 0 0;
  }

  &-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-family: 'Montserrat', sans-serif;
    font-style: normal;
    font-weight: 500;
    font-size: 15px;
    line-height: 18px;
    color: #333333;
  }

  &-body {
    padding: 30px 60px 60px;
    overflow: hidden;
  }

  &-close {
    cursor: pointer;
    max-width: 20px;
    height: 20px;
  }

  &-confirm {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.96);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24px;
    border-radius: 3px;
  }

  &-confirm-text {
    font-size: 15px;
    font-weight: 500;
    color: #333333;
    text-align: center;
    padding: 0 40px;
  }

  &-confirm-actions {
    display: flex;
    gap: 12px;
  }
}

.confirm-close {
  background-color: rgb(243, 243, 243);
  padding: 10px;
  border-radius: 8px;
  padding: 24px;
  padding-bottom: 20px;
  display: grid;
  grid-template-columns: 1fr 272px;
  gap: 16px;
  position: relative;
  font-weight: 500;

  > div {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;

    &:first-child {
      flex-direction: column;
      gap: 4px;
    }

    &:last-child {
      align-items: center;
      flex-wrap: nowrap;
    }

    .btn {
      margin: 0;
      width: 100%;
    }

    .btn.red:not(:hover):not(:focus) {
      background-color: transparent;
    }
  }
}

.modal.medium {
  .info-modal {
    max-width: 1382px;
  }

  .info-modal-body {
    position: relative;
    padding: 45px 24px 21px;
    overflow-y: auto;
    max-height: 85vh;
  }
}

.modal.medium-wide {
  .info-modal {
    max-width: 950px;
  }

  .info-modal-body {
    padding: 0;
  }
}

.modal-secondary {
  .info-modal-head {
    background-color: rgba(208, 235, 255, 1);
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }

  .info-modal {
    border-radius: 8px;
  }

  .info-modal-confirm {
    border-radius: 8px;
  }

  .info-modal-close {
    svg {
      fill: rgba(48, 168, 255, 1);
    }
  }
}

.modal-beru-create {
  .info-modal {
    max-width: calc(100vw - 40px);
  }

  .info-modal-body {
    position: relative;
  }

  .confirm-close {
    position: absolute;
    bottom: 60px;
    left: 60px;
    right: 60px;
  }
}

.modal-beru-detail {
  .info-modal {
    max-width: calc(100vw - 40px);
  }

  .info-modal-body {
    padding: 0;
  }
}

.modal-beru-tariffs {
  .info-modal-body {
    min-width: 471px;
    min-height: 300px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    &:has(.isOffline) {
      padding-bottom: 34px;
    }
  }
}
</style>